import { Button, Avatar, Typography, Tag, Modal, notification } from 'antd';
import { UserOutlined, PhoneOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { TCustomer, TSmartVariableDefinition } from '@/types/TSmartCRM';
import { getCustomerFieldTags } from '@/utils/SmartCRM/fieldUtils';
import { useState } from 'react';

const { Text } = Typography;

interface TCustomerCardProps {
	customer: TCustomer;
	fields: TSmartVariableDefinition[];
	onAction?: (action: string, customer: TCustomer) => void;
	deleteCustomer?: (leadId: string, variableId?: string) => Promise<void>;
}

export const CustomerCard = ({
	customer,
	fields,
	onAction,
	deleteCustomer,
}: TCustomerCardProps) => {
	const [isDeleting, setIsDeleting] = useState(false);
	const isVip = false;
	const allTags = getCustomerFieldTags(customer, fields);
	const visibleTags = allTags.slice(0, 3);
	const remainingCount = allTags.length - visibleTags.length;

	const handleDeleteCustomer = () => {
		Modal.confirm({
			title: 'Delete Customer',
			icon: <ExclamationCircleOutlined />,
			content: 'Are you sure you want to delete this customer? This action cannot be undone.',
			okText: 'Delete',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: async () => {
				if (!deleteCustomer) {
					return;
				}

				try {
					setIsDeleting(true);
					await deleteCustomer(customer.id);
					notification.success({
						message: 'Success',
						description: 'Customer deleted successfully',
					});
				} catch (error) {
					console.error('Error deleting customer:', error);
					notification.error({
						message: 'Error',
						description: 'Failed to delete customer. Please try again.',
					});
				} finally {
					setIsDeleting(false);
				}
			},
		});
	};

	const handleAction = (action: string, event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();

		if (action === 'delete') {
			handleDeleteCustomer();
		} else {
			onAction?.(action, customer);
		}
	};

	return (
		<div
			className={`bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
				isVip ? 'border-2 border-yellow-400' : 'border border-gray-200'
			} ${isDeleting ? 'opacity-50' : ''}`}
		>
			<div className="space-y-3">
				<div className="flex items-start justify-between">
					<div className="flex items-start space-x-3 flex-1">
						<Avatar size="small" icon={<UserOutlined />} className="bg-blue-500 flex-shrink-0" />
						<div className="min-w-0 flex-1">
							<div className="flex items-center gap-2">
								<Text className="font-semibold text-gray-900 text-sm truncate">
									{customer.name}
								</Text>
								{isVip && (
									<Tag color="gold" className="text-xs">
										VIP
									</Tag>
								)}
							</div>
							<div className="flex items-center gap-1 mt-1">
								<PhoneOutlined className="text-gray-400 text-xs" />
								<Text className="text-gray-500 text-xs truncate">{String(customer.number)}</Text>
							</div>
						</div>
					</div>
				</div>

				{visibleTags.length > 0 ? (
					<div className="space-y-2">
						{visibleTags.map((tag) => (
							<div key={tag.key}>
								<Tag color="blue" className="text-xs truncate max-w-full">
									<span className="truncate">
										{tag.label}: {tag.value}
									</span>
								</Tag>
							</div>
						))}
						{remainingCount > 0 && (
							<Text className="text-gray-400 text-xs">+{remainingCount} more</Text>
						)}
					</div>
				) : (
					<div className="py-2">
						<Tag color="default" className="text-xs">
							New customer
						</Tag>
					</div>
				)}

				<div className="flex justify-between pt-2 border-t border-gray-100">
					<Button
						type="text"
						size="small"
						className="text-blue-600 hover:text-blue-800 p-0 h-auto text-xs"
						onClick={(e) => handleAction('edit', e)}
						disabled={isDeleting}
					>
						Edit
					</Button>
					<Button
						type="text"
						size="small"
						className="text-red-600 hover:text-red-800 p-0 h-auto text-xs"
						onClick={(e) => handleAction('delete', e)}
						disabled={isDeleting}
					>
						{isDeleting ? 'Deleting...' : 'Delete'}
					</Button>
				</div>
			</div>
		</div>
	);
};
